import type { FC, FormEvent, ReactNode } from 'react'
import type { SessionUser } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { Loader2 } from 'lucide-react'
import { TextField } from '@/components/generals/form-field'
import { apiRequest, errorMessage } from '@/lib/client/api'

interface AuthFormProps {
    onSuccess: (user: SessionUser) => void
    onSwitch?: () => void
    staff?: boolean
}

export const LoginForm: FC<AuthFormProps> = ({ onSuccess, onSwitch, staff = false }): ReactNode => {
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setError(null)
        setBusy(true)

        try {
            const result = await apiRequest<{ user: SessionUser }>('/api/auth/login', { method: 'POST', body: { email, password, staff } })

            onSuccess(result.user)
        }
        catch (problem) {
            setError(errorMessage(problem))
        }
        finally {
            setBusy(false)
        }
    }

    return (
        <form onSubmit={submit} className='flex flex-col gap-4' noValidate>
            <TextField
                label='E-mailadres'
                type='email'
                autoComplete='email'
                value={email}
                onChange={event => setEmail(event.target.value)}
                required
            />

            <TextField
                label='Wachtwoord'
                type='password'
                autoComplete='current-password'
                value={password}
                onChange={event => setPassword(event.target.value)}
                required
            />

            {error && (
                <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive' role='alert'>
                    {error}
                </p>
            )}

            <button
                type='submit'
                disabled={busy}
                className='inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-60'
            >
                {busy && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
                Inloggen
            </button>

            <div className='flex flex-wrap items-center justify-between gap-2 text-sm'>
                <Link href={staff ? '/account/wachtwoord-vergeten?staff=1' : '/account/wachtwoord-vergeten'} className='text-muted-foreground underline-offset-4 hover:text-foreground hover:underline'>
                    Wachtwoord vergeten?
                </Link>

                {onSwitch && (
                    <button type='button' onClick={onSwitch} className='font-medium underline-offset-4 hover:underline'>
                        Nog geen account? Registreer
                    </button>
                )}
            </div>
        </form>
    )
}

export const RegisterForm: FC<AuthFormProps> = ({ onSuccess, onSwitch }): ReactNode => {
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [password, setPassword] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        setError(null)

        if (password.length < 8) {
            setError('Kies een wachtwoord van minimaal 8 tekens.')
            return
        }

        setBusy(true)

        try {
            const result = await apiRequest<{ user: SessionUser }>('/api/auth/register', { method: 'POST', body: { name, email, phone, password } })

            onSuccess(result.user)
        }
        catch (problem) {
            setError(errorMessage(problem))
        }
        finally {
            setBusy(false)
        }
    }

    return (
        <form onSubmit={submit} className='flex flex-col gap-4' noValidate>
            <TextField label='Volledige naam' autoComplete='name' value={name} onChange={event => setName(event.target.value)} required />

            <div className='grid gap-4 sm:grid-cols-2'>
                <TextField label='E-mailadres' type='email' autoComplete='email' value={email} onChange={event => setEmail(event.target.value)} required />
                <TextField label='Telefoonnummer' type='tel' autoComplete='tel' value={phone} onChange={event => setPhone(event.target.value)} optional />
            </div>

            <TextField
                label='Wachtwoord'
                type='password'
                autoComplete='new-password'
                hint='Minimaal 8 tekens.'
                value={password}
                onChange={event => setPassword(event.target.value)}
                required
            />

            {error && (
                <p className='rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive' role='alert'>
                    {error}
                </p>
            )}

            <button
                type='submit'
                disabled={busy}
                className='inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/85 disabled:opacity-60'
            >
                {busy && <Loader2 className='size-4 animate-spin' aria-hidden='true' />}
                Account aanmaken
            </button>

            <p className='text-xs leading-relaxed text-muted-foreground'>
                Met een account bewaart u ontwerpen, offertes en bestellingen op één plek. Lees ons{' '}
                <Link href='/privacy' className='underline underline-offset-3 hover:text-foreground'>
                    privacybeleid
                </Link>
                .
            </p>

            {onSwitch && (
                <button type='button' onClick={onSwitch} className='text-sm font-medium underline-offset-4 hover:underline'>
                    Heeft u al een account? Log in
                </button>
            )}
        </form>
    )
}
